import {
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	ListSubheader,
	SxProps,
	Theme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
	selectIsFiltersDrawerOpened,
	selectPersistedFilters,
} from "./filters.selector";
import {
	setFiltersDrawerOpened,
	setPersistedFilters,
	setLinqPredicates,
} from "./filtersSlice";
import useFetchFilters from "../../common/hooks/useFetchFilters";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import FilterRow from "./FilterRow";
import { PrimaryButton } from "../../common/components/CustomButton";
import CloseIcon from "@mui/icons-material/Close";

import * as yup from "yup";
import {
	buildPredicates,
	removeEmptyListsAndNullValues,
	setSelectedFilterValues,
} from "./utils";
import { Filter, FilterType } from "../metadata/metadata.module";
import { useNavigate, useSearchParams } from "react-router-dom";
import _ from "lodash";
import { CANCEL_FILTER } from "./filters.constant";

const style: { [key: string]: SxProps<Theme> } = {
	drawer: {
		width: 400,
	},
	list: {
		width: "100%",
		maxWidth: 389,
		bgcolor: "background.paper",
	},
	logout: {
		color: "red",
	},
	header: {
		color: "#171D21",
		fontSize: 28,
		weight: 400,
	},
};

interface TenderAndPricingFiltersDrawerProps {
	page: string;
	id?: number;
	filtersResetAt?: Date;
	setSelectedtemplate?: React.Dispatch<SetStateAction<string | null>>;
	setActiveOnly?: React.Dispatch<SetStateAction<"all" | "active">>;
}

export default function TenderAndPricingFiltersDrawer(
	props: TenderAndPricingFiltersDrawerProps
) {
	const navigate = useNavigate();
	const isOpened = useSelector(selectIsFiltersDrawerOpened);
	const page = useMemo(() => props.page, [props.page]);
	const dispatch = useDispatch();
	const { data: filtersData } = useFetchFilters(page, props.id);
	const persistedFilters = useSelector(selectPersistedFilters);
	const [initialFilterValues, setInitialFilterValues] = useState<{
		[k: string]: any;
	}>({});
	const [searchParams] = useSearchParams();
	const queryFilters = searchParams.get("filters") ?? "";

	// Browse filters and create formik bag accordingly with blank values
	const blankFilterValues = useMemo(() => {
		let obj: { [k: string]: any } = {};
		if (filtersData) {
			filtersData.forEach((f: Filter) => {
				if (
					f.filter_type === FilterType.alphanumeric ||
					f.filter_type === FilterType.list_input ||
					f.filter_type === FilterType.list_input_not_in
				) {
					obj[`${f.field_key}`] = "";
				} else {
					if (f.filter_type === FilterType.numeric) {
						obj[`${f.field_key}`] = [null, null];
					} else {
						obj[`${f.field_key}`] = null;
					}
				}
			});
		}

		return obj;
	}, [filtersData]);
	function handleActiveOnlyToggleButton(values: any) {
		const activeFilterValue = _.find(
			values.pricings_status_not_in,
			CANCEL_FILTER
		)
			? "active"
			: "all";
		if (props.setActiveOnly) {
			props.setActiveOnly(activeFilterValue);
		}
	}

	const submitFilters = (values: any, close: boolean) => {
		const { predicates, linqPredicates } = buildPredicates(
			filtersData,
			values,
			page
		);

		const odataString = `"${predicates.join(" and ")}"`;
		const filterTemplate = close
			? "CUSTOM"
			: persistedFilters.filters?.tenders_filter_template;

		const filteredValues = removeEmptyListsAndNullValues(values);
		const filtersToPersist = JSON.stringify(filteredValues);
		const params = new URLSearchParams();
		params.set("filters", filtersToPersist);
		if (page === "Tenders") {
			dispatch(
				setPersistedFilters({
					...persistedFilters.filters,
					tenders_filter_template: filterTemplate,
					tenders_filters: filtersToPersist,
					tenders_filters_count: predicates.length.toString(),
					tenders_query: odataString,
				})
			);
		}
		if (page === "Tenders Workflow view") {
			dispatch(
				setPersistedFilters({
					...persistedFilters.filters,
					tenders_workflow_view_filter_template: filterTemplate,
					tenders_workflow_view_filters: filtersToPersist,
					tenders_workflow_view_filters_count:
						predicates.length.toString(),
					tenders_workflow_view_query: odataString,
				})
			);
		}
		if (page === "Pricings") {
			handleActiveOnlyToggleButton(values);
			dispatch(
				setPersistedFilters({
					...persistedFilters.filters,
					pricings_filters: filtersToPersist,
					pricings_filters_count: predicates.length.toString(),
					pricings_query: odataString,
				})
			);
			navigate("?" + params.toString());
		}

		dispatch(
			setLinqPredicates({
				page,
				predicates: linqPredicates,
			})
		);

		if (close) {
			dispatch(
				setFiltersDrawerOpened({
					opened: false,
				})
			);

			if (props.setSelectedtemplate) {
				props.setSelectedtemplate("CUSTOM");
				if (page === "Tenders") {
					dispatch(
						setPersistedFilters({
							...persistedFilters.filters,
							tenders_filter_template: "CUSTOM",
							tenders_filters: filtersToPersist,
							tenders_filters_count: predicates.length.toString(),
							tenders_query: odataString,
						})
					);
				}
				if (page === "Pricings") {
					dispatch(
						setPersistedFilters({
							...persistedFilters.filters,
							tenders_filter_template: "CUSTOM",
							tenders_filters: filtersToPersist,
							tenders_filters_count: predicates.length.toString(),
							tenders_query: odataString,
						})
					);
				}
			}
		}
	};

	useEffect(() => {
		if (!page) {
			return;
		}

		if (!filtersData.length) {
			return;
		}
		if (queryFilters) {
			const jsonQueryFilters = JSON.parse(queryFilters);
			const { predicates } = buildPredicates(
				filtersData,
				jsonQueryFilters,
				page
			);
			const odataString = `"${predicates.join(" and ")}"`;
			dispatch(
				setPersistedFilters({
					...persistedFilters.filters,
					pricings_filters: queryFilters,
					pricings_filters_count: predicates.length.toString(),
					pricings_query: odataString,
				})
			);
		}

		const tenderFilters = persistedFilters.filters?.tenders_filters ?? "";
		const pricingFilters = persistedFilters.filters?.pricings_filters ?? "";
		const tenderWorkflowViewFilters =
			persistedFilters.filters?.tenders_workflow_view_filters ?? "";

		let obj;
		switch (page) {
			case "Tenders":
				obj = setSelectedFilterValues(filtersData, tenderFilters);
				break;
			case "Tenders Workflow view":
				obj = setSelectedFilterValues(
					filtersData,
					tenderWorkflowViewFilters
				);
				break;
			default:
				obj = setSelectedFilterValues(filtersData, pricingFilters);
				break;
		}

		if (obj) {
			setInitialFilterValues(obj);
		} else {
			setInitialFilterValues({ ...blankFilterValues });
		}
		if (props.filtersResetAt) {
			submitFilters(obj, false);
		}
	}, [
		filtersData,
		page,
		isOpened,
		props.filtersResetAt,
		persistedFilters.filters?.tenders_filters,
		persistedFilters.filters?.pricings_filters,
		persistedFilters.filters?.tenders_workflow_view_filters,
	]);

	return (
		<Drawer
			anchor={"left"}
			open={isOpened}
			onClose={() => dispatch(setFiltersDrawerOpened({ opened: false }))}
			PaperProps={{
				sx: style.drawer,
			}}
		>
			<List
				sx={style.list}
				component="nav"
				aria-labelledby="filters"
				subheader={
					<ListSubheader
						sx={style.header}
						component="div"
						id="filters-header"
					>
						{page} filters
						<ListItemSecondaryAction
							onClick={() =>
								dispatch(
									setFiltersDrawerOpened({
										opened: false,
									})
								)
							}
						>
							<IconButton edge="end" aria-label="delete">
								<CloseIcon />
							</IconButton>
						</ListItemSecondaryAction>
					</ListSubheader>
				}
			>
				<Formik
					initialValues={initialFilterValues}
					validationSchema={() => {
						const validationObject: any = {};
						if (filtersData) {
							filtersData.forEach((f: Filter) => {
								if (f.filter_type === FilterType.numeric) {
									validationObject[f.field_key] = yup
										.array()
										.test(
											"is-valid-range",
											"Not a valid range",
											(value) => {
												if (!value) {
													return true;
												}
												if (value.length < 2) {
													return true;
												}

												if (value[0] && value[1]) {
													const val1 = Number(
														value[0]
													);
													const val2 = Number(
														value[1]
													);
													return val1 <= val2;
												}

												return true;
											}
										)
										.nullable();
								}
							});
						}
						return yup.object(validationObject);
					}}
					enableReinitialize={true}
					validateOnChange={false}
					validateOnBlur={false}
					onSubmit={(values) => {
						submitFilters(values, true);
					}}
				>
					{({ handleSubmit, setValues }) => (
						<form onSubmit={handleSubmit}>
							{filtersData.map((filter: Filter) => {
								return (
									<ListItem key={filter.field_key}>
										<ListItemText
											primary={
												<h6>{filter.display_name}</h6>
											}
											secondary={
												<FilterRow filter={filter} />
											}
										/>
									</ListItem>
								);
							})}
							<ListItem key="submit">
								<ListItemText
									primary={
										<>
											<PrimaryButton
												data-testid="apply-filters"
												text="Apply filters"
												type="submit"
												color="primary"
												sx={{
													width: 130,
												}}
											/>
											<PrimaryButton
												text="Reset"
												type="button"
												color="secondary"
												sx={{
													width: 130,
													marginLeft: "10px",
												}}
												onClick={() => {
													setValues(
														blankFilterValues
													);
												}}
											/>
										</>
									}
								/>
							</ListItem>
						</form>
					)}
				</Formik>
			</List>
		</Drawer>
	);
}
